import { ProfileType } from '@/shared/api';
import { useAuthStore } from '@/shared/store/auth.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { log } from '@/shared/utils/log';

import { CollectionsSjMap } from '../types';

import { isActualVersion } from './helpers';
import { Migrator } from './migrator';
import { MigratedMigrationCollectionsData, MigratorError, OnUserActionRequiredFn } from './types';

type MigrationResult =
  /* success */
  | { data: null; error: null }
  | {
      data: {
        schemaVersion: number;
        data: MigratedMigrationCollectionsData;
      };
      error: null;
    }
  /* error */
  | { data: null; error: string }
  | { data: null; error: MigratorError };

const supportedProfileType = (type: ProfileType | null) => {
  return type !== null && (type === ProfileType.MAIN_USER || type === ProfileType.BENEFICIARY);
};

export const useMigrator = (onUserActionRequired: OnUserActionRequiredFn) => {
  const schemaVersionAuth = useAuthStore((s) => s.schemaVersion);
  const profileType = useProfileStore((s) => s.profileType);
  const { keyPair } = useKeyPairStore();

  // TODO refactor auth flow
  const schemaVersion = useAuthStore.getState().schemaVersion;
  log.debug(`[useMigrator] ${String(schemaVersionAuth)} / ${schemaVersion}`);

  const migrateCollections = async ({
    initialCollections,
  }: {
    initialCollections: CollectionsSjMap;
  }): Promise<MigrationResult> => {
    if (!supportedProfileType(profileType)) {
      return { data: null, error: `Migration unsupported for profileType: ${profileType}` };
    }

    if (!keyPair) {
      return { data: null, error: 'Empty keyPair' };
    }

    const migrator = new Migrator(schemaVersion ?? 1, keyPair, onUserActionRequired);

    const { error, migratedCollections } = await migrator.migration(initialCollections);

    if (error) {
      return { data: null, error: String(error) };
    }

    if (migratedCollections) {
      return { data: migratedCollections, error: null };
    }

    return { data: null, error: null };
  };

  const shouldMigrate = () => {
    if (!schemaVersion) {
      log.error(`[useMigrator] Invalid schemaVersion: ${schemaVersion}`);
      throw new Error(`Invalid schemaVersion: ${schemaVersion}`);
    }
    return !isActualVersion(schemaVersion);
  };

  return { migrateCollections, shouldMigrate };
};
